/**
 * Theme provider — mounted in main.tsx, drives the `dark` class on <html>.
 *
 * Tailwind is configured with `darkMode: 'class'`, and the semantic colour
 * tokens live in index.css under `:root` and `.dark`.
 *
 * THE ACCOUNT IS THE SOURCE OF TRUTH, not this browser. `users.theme` has
 * been settable through PUT /auth/me/preferences all along and was never
 * returned by anything, so the only place a theme could live was localStorage
 * — which meant it was a property of the machine you happened to be sitting
 * at. /auth/me returns it now, and this provider adopts it whenever the
 * profile loads, including after signing in somewhere new.
 *
 * localStorage is kept, but demoted to a cache with one job: the pre-paint
 * script in index.html reads it to set the class before first paint, so the
 * page does not flash light before the profile arrives. It is written on every
 * change and overwritten by the account whenever the two disagree.
 *
 * This file previously set six CSS custom properties of its own
 * (--bg-primary and friends) that no stylesheet ever read. They are gone: the
 * tokens in index.css are the real ones, and a second inert set beside them
 * would only invite someone to style against the wrong one.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { useAuthStore } from '@/stores/authStore';
import { apiPut } from '@/utils/api';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

const THEME_STORAGE_KEY = 'spaciora-theme';

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const VALID: readonly Theme[] = ['light', 'dark', 'system'];

function cachedTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    return v && VALID.includes(v) ? v : 'light';
  } catch {
    return 'light';
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Seeded from the cache so the first paint matches what the pre-paint script
  // already put on <html>. The account overrides it as soon as it arrives.
  const [theme, setThemeState] = useState<Theme>(cachedTheme);

  const accountTheme = useAuthStore((s) => s.user?.theme) as Theme | undefined;

  // The OS preference is STATE, not something read during render.
  //
  // It used to be read inline and the media-query listener tried to force a
  // re-render with `setThemeState('system')` — setting a value identical to
  // the one already held, which React discards, so the effect below never
  // re-ran and changing the OS theme did nothing. Holding it as state means
  // the change actually propagates.
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystemTheme);

  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  // The account's value wins whenever the two disagree.
  //
  // This path calls setThemeState directly and NOT setTheme, which is what
  // keeps adopting a remote value from being echoed straight back to the
  // server as if the user had just picked it. `theme` is deliberately not a
  // dependency either: this fires when the ACCOUNT changes, not when the local
  // value does.
  useEffect(() => {
    if (!accountTheme || !VALID.includes(accountTheme)) return;
    setThemeState((current) => {
      if (current === accountTheme) return current;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, accountTheme);
      } catch {
        /* private browsing — the account still holds it */
      }
      return accountTheme;
    });
  }, [accountTheme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch {
      /* the PUT below is what actually persists it */
    }
    // Persist to the account. Deliberately not awaited and deliberately not
    // rolled back on failure: the theme has already applied, and reverting the
    // page under someone because a request failed is worse than a preference
    // that did not stick. It retries itself the next time they pick one.
    const { user, updateUser } = useAuthStore.getState();
    if (!user) return;
    updateUser({ ...user, theme: newTheme });
    void apiPut('/auth/me/preferences', { theme: newTheme }).catch(() => {
      /* offline or logged out; the local value stands until next change */
    });
  }, []);

  // Apply the class. The colours themselves come from the token definitions
  // in index.css, which is why there is nothing to set here but this.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedTheme === 'dark');
    // Keeps native controls, scrollbars and autofill in step with the theme.
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    // Catch a change that happened between first render and this effect.
    setSystemTheme(mq.matches ? 'dark' : 'light');
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
